import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from "recharts";
import ThemedChartTooltip, { ThemedTooltipBox } from "../../../shared/ThemedChartTooltip";
import { fmtDays, fmtNum } from "../utils";

// ─── WaitingOnThemChart ──────────────────────────────────────────────────────────
// Horizontal bars: days blocked per client, waiting on them. Spec says "this number
// has never existed here. It turns 'they're slow' into evidence." Recharts replaces
// the hand-rolled div bars (tracker §"Replace plain SVG charts with recharts").
//
// Props: { data } where data is array of { client, total_days_blocked, blocked_items, worst }.
//   • Sorted by blocked_items descending (most blocked first) — tracker v2 collapse.
//   • Worst client highlighted red (#ef4444); rest blue (#32B1FF).
//   • Shows top 10 by default with a "Show all" toggle (tracker v2 §"Collapse…").
//   • Tooltip shows full client name, blocked_items count, and worst-case duration.
//   • null/undefined/[] data → "No client waiting data yet".
const COLORS = { worst: "#ef4444", rest: "#32B1FF" };

const TOP_N = 10;

const WaitingOnThemChart = ({ data, openDrill }) => {
  const [showAll, setShowAll] = useState(false);

  // Empty or missing data — spec: show "No client waiting data yet" when empty/missing
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
        <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Waiting on Them</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Days blocked per client — turning "they're slow" into evidence.</p>
        <div className="mt-4 h-48 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No client waiting data yet</p>
        </div>
      </div>
    );
  }

  // Sort by blocked_items descending (most blocked first). Coerce defensively.
  // (tracker v2 step 3 — primary sort is blocked_items, ties fall back to days.)
  const rows = [...data]
    .map((r) => ({
      ...r,
      total_days_blocked: Number(r.total_days_blocked) || 0,
      blocked_items: Number(r.blocked_items) || 0,
      worst: r.worst == null ? null : Number(r.worst),
    }))
    .sort((a, b) => b.blocked_items - a.blocked_items || b.total_days_blocked - a.total_days_blocked);

  const visibleRows = showAll ? rows : rows.slice(0, TOP_N);
  const maxBlocked = Math.max(1, ...rows.map((r) => r.total_days_blocked));

  // Dynamic height: ~350px when collapsed to 10; grows with row count when expanded,
  // capped at 600px so it never gets unwieldy (tracker v2 steps 6–7).
  const chartHeight = showAll
    ? Math.min(600, Math.max(400, rows.length * 35))
    : 350;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-bold text-gray-800 dark:text-gray-100">Waiting on Them</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Days blocked per client — turning "they're slow" into evidence.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.worst }} />
            Worst
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS.rest }} />
            Others
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={visibleRows}
            margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={{ stroke: "#374151" }}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="client"
              tick={{ fontSize: 12, fill: "#9CA3AF" }}
              tickLine={false}
              axisLine={false}
              width={180}
              tickFormatter={(v) => (v && v.length > 26 ? v.slice(0, 25) + "…" : v)}
            />
            <ThemedChartTooltip
              cursor={{ fill: "rgba(156,163,175,0.12)" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const r = payload[0].payload;
                return (
                  <ThemedTooltipBox title={r.client}>
                    <div>{fmtDays(r.total_days_blocked)} blocked</div>
                    <div>{fmtNum(r.blocked_items)} item{r.blocked_items !== 1 ? "s" : ""}</div>
                    {r.worst != null && <div style={{ marginTop: 4 }}>worst: {fmtDays(r.worst)}</div>}
                  </ThemedTooltipBox>
                );
              }}
            />
            <Bar
              dataKey="total_days_blocked"
              radius={[0, 4, 4, 0]}
              barSize={22}
              isAnimationActive={false}
              onClick={(d) => {
                // Recharts Bar onClick passes (data, index, event); data.payload
                // is the underlying row. Drill to the clicked client (prefer the
                // client id, fall back to the client name).
                const row = d?.payload ?? d;
                if (row) {
                  const id = row.client_id ?? row.client;
                  if (id != null) openDrill?.("client_waiting", id);
                }
              }}
            >
              {visibleRows.map((r, i) => (
                <Cell key={r.client || i} fill={r.total_days_blocked === maxBlocked ? COLORS.worst : COLORS.rest} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expand / collapse toggle — only when there are more than TOP_N clients */}
      {rows.length > TOP_N && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline-offset-2 hover:underline focus:outline-none"
          >
            {showAll ? "Show fewer" : `Show all ${rows.length} clients`}
          </button>
        </div>
      )}
    </div>
  );
};

export default WaitingOnThemChart;
